import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaSearch, 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaUserShield,
  FaUserCheck,
  FaBan,
  FaCheckCircle,
  FaTimesCircle,
  FaSync,
  FaFilter,
  FaDownload
} from '../../../utils/iconUtils';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  getAllUsers,
  updateUserRole,
  applyPermissionPreset,
  UserData,
  getSimplifiedPermissionPresets,
  UserPermissions,
  updateUserPermissions
} from '../../../firebase/usersFirestoreService';
import { useUI } from '../../../components/common/UIMessageProvider';
import SidebarVisibilityManager from './SidebarVisibilityManager';
import { FaColumns } from 'react-icons/fa';

interface UserWithActions extends UserData {
  isAdmin?: boolean;
  isViewer?: boolean;
  isEditor?: boolean;
}

const UserManagement: React.FC = () => {
  const { currentUser, isSuperAdmin, canPerformAction } = useAuth();
  const ui = useUI();
  const [users, setUsers] = useState<UserWithActions[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithActions[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [presetFilter, setPresetFilter] = useState('all');
  
  // Permissions for current user
  const canViewUsers = canPerformAction('users', 'view');
  const canEditUsers = canPerformAction('users', 'edit');
  const canChangeRole = canPerformAction('users', 'edit'); // Using edit for role change
  const canExportData = false; // You can add this to permissions if needed
  // Add these new states to UserManagement.tsx
  type PartialPermissions = Partial<{ [K in keyof UserPermissions]: Partial<UserPermissions[K]> }>;
  const [selectedUser, setSelectedUser] = useState<UserWithActions | null>(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [editingPermissions, setEditingPermissions] = useState<PartialPermissions>({});

  // Add this state to UserManagement.tsx
const [showSidebarManager, setShowSidebarManager] = useState(false);
const [selectedUserForSidebar, setSelectedUserForSidebar] = useState<UserWithActions | null>(null);

// Add this function to handle granular permission editing
const handleEditGranularPermissions = (user: UserWithActions) => {
  setSelectedUser(user);
  setEditingPermissions((user.permissions || {}) as PartialPermissions);
  setShowPermissionModal(true);
};

const handleOpenSidebarManager = (user: UserWithActions) => {
  setSelectedUserForSidebar(user);
  setShowSidebarManager(true);
};


// Add this function to save granular permissions
const handleSaveGranularPermissions = async () => {
  if (!selectedUser || !canEditUsers) return;

  const ok = await ui.askConfirm({
    title: 'Update Permissions',
    message: `Are you sure you want to update permissions for ${selectedUser.email}?`
  });
  
  if (!ok) return;

  try {
    // Use updateUserPermissions which merges nested objects
    const result = await updateUserPermissions(selectedUser.uid, editingPermissions as any);
    if (result.success) {
      ui.showMessage('Permissions updated successfully');
      setShowPermissionModal(false);
      fetchUsers(); // Refresh the list
    } else {
      ui.showMessage(`Error: ${result.error}`, { type: 'error' });
    }
  } catch (error: any) {
    ui.showMessage('Error updating permissions', { type: 'error' });
    console.error('Error updating permissions:', error);
  }
};

  // Fetch all users (except super_admin)
  const fetchUsers = useCallback(async () => {
    if (!canViewUsers) {
      ui.showMessage('You do not have permission to view users', { type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const result = await getAllUsers(true); // Include admins
      
      if (result.success) {
        // Filter out super_admin from the list
        const filtered = result.data.filter(user => 
          user.uid !== currentUser?.uid && // Don't show current user
          user.role !== 'super_admin' // Don't show super admins
        ).map(user => ({
          ...user,
          isAdmin: user.role === 'admin',
          isViewer: user.permissionPreset === 'viewer',
          isEditor: user.permissionPreset === 'editor'
        }));
        
        setUsers(filtered);
        setFilteredUsers(filtered);
      } else {
        ui.showMessage(`Error: ${result.error}`, { type: 'error' });
      }
    } catch (error: any) {
      ui.showMessage('Error fetching users', { type: 'error' });
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [canViewUsers, currentUser?.uid, ui]);

  // Apply filters
  useEffect(() => {
    let filtered = [...users];
    
    // Apply role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }
    
    // Apply preset filter
    if (presetFilter !== 'all') {
      filtered = filtered.filter(user => user.permissionPreset === presetFilter);
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.email?.toLowerCase().includes(searchLower) ||
        user.displayName?.toLowerCase().includes(searchLower) ||
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredUsers(filtered);
  }, [users, roleFilter, presetFilter, searchTerm]);

  useEffect(() => {
    if (canViewUsers) {
      fetchUsers();
    }
  }, [canViewUsers, fetchUsers]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (!canChangeRole) {
      ui.showMessage('You do not have permission to change user roles', { type: 'error' });
      return;
    }

    const ok = await ui.askConfirm({
      title: 'Change User Role',
      message: `Are you sure you want to change this user's role to ${newRole}?`
    });
    
    if (!ok) return;

    try {
      const result = await updateUserRole(userId, newRole, true); // true = apply default permissions
      if (result.success) {
        ui.showMessage('User role updated successfully');
        fetchUsers(); // Refresh the list
      } else {
        ui.showMessage(`Error: ${result.error}`, { type: 'error' });
      }
    } catch (error: any) {
      ui.showMessage('Error updating user role', { type: 'error' });
      console.error('Error updating role:', error);
    }
  };

  const handlePermissionPresetChange = async (userId: string, presetId: string) => {
    if (!canEditUsers) {
      ui.showMessage('You do not have permission to edit user permissions', { type: 'error' });
      return;
    }

    const ok = await ui.askConfirm({
      title: 'Change Permission Preset',
      message: `Are you sure you want to apply the ${presetId} preset to this user?`
    });
    
    if (!ok) return;

    try {
      const result = await applyPermissionPreset(userId, presetId);
      if (result.success) {
        ui.showMessage('User permissions updated successfully');
        fetchUsers(); // Refresh the list
      } else {
        ui.showMessage(`Error: ${result.error}`, { type: 'error' });
      }
    } catch (error: any) {
      ui.showMessage('Error updating permissions', { type: 'error' });
      console.error('Error updating permissions:', error);
    }
  };

  const handleToggleActive = async (userId: string, currentStatus: boolean) => {
    if (!canEditUsers) {
      ui.showMessage('You do not have permission to edit users', { type: 'error' });
      return;
    }

    const newStatus = !currentStatus;
    const ok = await ui.askConfirm({
      title: newStatus ? 'Activate User' : 'Deactivate User',
      message: `Are you sure you want to ${newStatus ? 'activate' : 'deactivate'} this user?`
    });
    
    if (!ok) return;

    // You would need to implement updateUserStatus function in usersFirestoreService
    // For now, just show a message
    ui.showMessage(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
  };

  const getPermissionPresets = () => {
    return getSimplifiedPermissionPresets();
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Admin</span>;
      case 'moderator':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Moderator</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">User</span>;
    }
  };

  const getPresetBadge = (preset: string) => {
    switch (preset) {
      case 'viewer':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Viewer</span>;
      case 'editor':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Editor</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Custom</span>;
    }
  };

  if (!canViewUsers) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FaBan className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700">Access Denied</h3>
          <p className="text-gray-500 mt-2">You do not have permission to view users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-soft p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-800">User Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage user roles and permissions (excluding Super Admins)
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {canExportData && (
              <button
                className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={!canExportData}
              >
                <FaDownload className="h-4 w-4" />
                Export
              </button>
            )}
            
            <button
              onClick={fetchUsers}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FaSync className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap gap-3">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admins</option>
            <option value="moderator">Moderators</option>
            <option value="user">Users</option>
          </select>

          {/* Preset Filter */}
          <select
            value={presetFilter}
            onChange={(e) => setPresetFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Presets</option>
            <option value="viewer">Viewers</option>
            <option value="editor">Editors</option>
            <option value="custom">Custom</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-3 text-sm text-gray-600">Loading users...</p>
            </div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center p-8">
            <FaUserShield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700">No users found</h3>
            <p className="text-gray-500 mt-2">
              {users.length === 0 ? 'No users to display' : 'No users match your filters'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permission Preset
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.uid} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <FaUserCheck className="h-5 w-5 text-gray-600" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {user.displayName || `${user.firstName} ${user.lastName}`}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-4 py-3">
                      {getPresetBadge(user.permissionPreset || 'custom')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        {user.isActive ? (
                          <>
                            <FaCheckCircle className="h-4 w-4 text-green-500" />
                            <span className="ml-2 text-sm text-gray-900">Active</span>
                          </>
                        ) : (
                          <>
                            <FaTimesCircle className="h-4 w-4 text-red-500" />
                            <span className="ml-2 text-sm text-gray-900">Inactive</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        {/* Role Change Dropdown */}
                        {canChangeRole && (
                          <select
                            value={user.role}
                            onChange={(e) => handleRoleChange(user.uid, e.target.value)}
                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            disabled={!canChangeRole}
                          >
                            <option value="user">User</option>
                            <option value="moderator">Moderator</option>
                            <option value="admin">Admin</option>
                          </select>
                        )}

                        {/* Permission Preset Dropdown */}
                        {canEditUsers && (
                          <select
                            value={user.permissionPreset || 'custom'}
                            onChange={(e) => handlePermissionPresetChange(user.uid, e.target.value)}
                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            disabled={!canEditUsers}
                          >
                            <option value="viewer">Viewer</option>
                            <option value="editor">Editor</option>
                            <option value="custom">Custom</option>
                          </select>
                        )}

                        {/* Toggle Active Status */}
                        {canEditUsers && (
                          <button
                            onClick={() => handleToggleActive(user.uid, user.isActive || false)}
                            className={`px-2 py-1 text-sm rounded ${
                              user.isActive 
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                            disabled={!canEditUsers}
                          >
                            {user.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                        {/* Granular Permissions */}
                        {canEditUsers && (
                          <button
                            onClick={() => handleEditGranularPermissions(user)}
                            className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                            title="Edit granular permissions"
                          >
                            <FaUserShield className="h-4 w-4" />
                          </button>
                        )}
                        <button
  onClick={() => handleOpenSidebarManager(user)}
  className="flex items-center space-x-1 px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
  title="Manage sidebar visibility"
>
  <FaColumns className="h-4 w-4" />
  <span>Sidebar</span>
</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            // Add this modal at the end of the component (before the closing div)
{showSidebarManager && selectedUserForSidebar && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-soft p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">
            Sidebar Visibility Manager
          </h2>
          <p className="text-sm text-gray-600">
            Control which sidebar items {selectedUserForSidebar.email} can see
          </p>
        </div>
        <button
          onClick={() => {
            setShowSidebarManager(false);
            setSelectedUserForSidebar(null);
          }}
          className="text-gray-400 hover:text-gray-600"
        >
          <FaTimesCircle className="h-6 w-6" />
        </button>
      </div>
      
      <SidebarVisibilityManager 
        user={selectedUserForSidebar}
        onUpdate={() => {
          fetchUsers(); // Refresh user list
        }}
      />
    </div>
  </div>
)}
          </div>
        )}

        {/* Summary */}
        {!loading && filteredUsers.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-600">
              Showing {filteredUsers.length} of {users.length} users
              <span className="mx-2">•</span>
              <span className="text-green-600 font-medium">
                {users.filter(u => u.isEditor).length} Editors
              </span>
              <span className="mx-2">•</span>
              <span className="text-gray-600 font-medium">
                {users.filter(u => u.isViewer).length} Viewers
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Permission Preset Info */}
      <div className="bg-white rounded-xl shadow-soft p-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Permission Presets</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-900">Viewer</span>
              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                Read Only
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Can only view content, no editing rights
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>✓ Can view all sections (if enabled)</li>
              <li>✗ Cannot add, edit, or delete</li>
              <li>✗ Cannot export or print</li>
              <li>✗ Cannot manage users</li>
            </ul>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-900">Editor</span>
              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                Full Edit
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Full editing rights for all sections except user management
            </p>
            <ul className="text-sm text-gray-500 space-y-1">
              <li>✓ Can view all sections</li>
              <li>✓ Can add, edit, and delete content</li>
              <li>✓ Can export and print</li>
              <li>✗ Cannot manage users</li>
            </ul>
          </div>
        </div>
      </div>
      {/* Add this modal component inside the UserManagement component return statement, before the closing div */}
{showPermissionModal && selectedUser && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-soft p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">
          Edit Permissions for {selectedUser.email}
        </h2>
        <button
          onClick={() => setShowPermissionModal(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          <FaTimesCircle className="h-6 w-6" />
        </button>
      </div>
      
      <div className="space-y-6">
        {/* Accounting Section */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Accounting Access</h3>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={!!(editingPermissions.accounting?.view)}
                onChange={(e) => {
                  setEditingPermissions(prev => ({
                    ...prev,
                    accounting: {
                      ...prev.accounting,
                      view: e.target.checked,
                      // If view is disabled, disable all other permissions
                      create: e.target.checked ? prev.accounting?.create || false : false,
                      edit: e.target.checked ? prev.accounting?.edit || false : false,
                      delete: e.target.checked ? prev.accounting?.delete || false : false,
                      approve: e.target.checked ? prev.accounting?.approve || false : false
                    }
                  }));
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-900">
                {editingPermissions.accounting?.view ? 'Visible' : 'Hidden'}
              </span>
            </label>
          </div>
          
          {editingPermissions.accounting?.view && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={!!editingPermissions.accounting?.create}
                  onChange={(e) => setEditingPermissions(prev => ({
                    ...prev,
                    accounting: { ...prev.accounting, create: e.target.checked }
                  }))}
                  className="h-4 w-4 text-primary-600 rounded"
                />
                <span className="text-sm text-gray-700">Create</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={!!editingPermissions.accounting?.edit}
                  onChange={(e) => setEditingPermissions(prev => ({
                    ...prev,
                    accounting: { ...prev.accounting, edit: e.target.checked }
                  }))}
                  className="h-4 w-4 text-primary-600 rounded"
                />
                <span className="text-sm text-gray-700">Edit</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={!!editingPermissions.accounting?.delete}
                  onChange={(e) => setEditingPermissions(prev => ({
                    ...prev,
                    accounting: { ...prev.accounting, delete: e.target.checked }
                  }))}
                  className="h-4 w-4 text-primary-600 rounded"
                />
                <span className="text-sm text-gray-700">Delete</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={!!editingPermissions.accounting?.approve}
                  onChange={(e) => setEditingPermissions(prev => ({
                    ...prev,
                    accounting: { ...prev.accounting, approve: e.target.checked }
                  }))}
                  className="h-4 w-4 text-primary-600 rounded"
                />
                <span className="text-sm text-gray-700">Approve</span>
              </label>
            </div>
          )}
          
          <p className="text-xs text-gray-500 mt-3">
            When disabled, the Accounting section will be completely hidden from this user's sidebar.
          </p>
        </div>
        
        {/* Other sections can be added similarly */}
        
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowPermissionModal(false)}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveGranularPermissions}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default UserManagement;